import { memo, Suspense, useCallback } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ProtectedRoute } from "./ProtectedRoute";
import { NonProtectedRoute } from "./NonProtectedRoute";
import { PublicRoute } from "./PublicRoute";
import { AppRoutesProps, routeConfig } from "../config/routeConfig";
import { Footer } from "@/frontend/widgets/Footer/Footer";
import { getRoutePrivacyNotice, getRouteTermsOfUse, getRouteWeAreDeploying } from "@/frontend/shared/const/router";
import styles from "./AppRouter.module.scss";
import { WeAreDeployingPage } from "@/frontend/pages/WeAreDeployingPage";
const AppRouter = () => {
  const { pathname } = useLocation();
  const privacyNoticeRoute = getRoutePrivacyNotice();
  const termsAndConditionsRoute = getRouteTermsOfUse();
  const weAreDeployingRoute = getRouteWeAreDeploying();
  const isPrivacyOrTermsPage = pathname === privacyNoticeRoute || pathname === termsAndConditionsRoute;
  const shouldRedirect = import.meta.env.VITE_REDIRECT_ALL_ROUTES === "true";

  if (shouldRedirect) {
    return (
      <Routes>
        <Route path="*" element={<Navigate to={weAreDeployingRoute} replace />} />
        <Route
          path={weAreDeployingRoute}
          element={
            <PublicRoute>
              <WeAreDeployingPage />
            </PublicRoute>
          }
        />
      </Routes>
    );
  }

  const renderWithWrapper = useCallback(
    (route: AppRoutesProps) => {
      const element = (
        <Suspense fallback={<div>Loading...</div>}>
          <div>
            <div className={styles.contentFooterWrapper}>
              {route.element}
              {!isPrivacyOrTermsPage && <Footer />}
            </div>
          </div>
        </Suspense>
      );

      switch (true) {
        case route.routePrivilege === "protected":
          return (
            <Route
              key={route.path}
              path={route.path}
              element={<ProtectedRoute roleAccess={route.roleAccess}>{element}</ProtectedRoute>}
            />
          );
        case route.routePrivilege === "nonprotected":
          return (
            <Route key={route.path} path={route.path} element={<NonProtectedRoute>{element}</NonProtectedRoute>} />
          );
        case route.routePrivilege === "public":
        default:
          return <Route key={route.path} path={route.path} element={<PublicRoute>{element}</PublicRoute>} />;
      }
    },
    [isPrivacyOrTermsPage]
  );

  return <Routes>{Object.values(routeConfig).map(renderWithWrapper)}</Routes>;
};

export default memo(AppRouter);
